import { Frequency, pence } from '@cf/shared';
import { stampDutyLandTax } from '@cf/tax';
import { resolveEscalationBpsForYear } from './assumptions.js';
import { ageAtTaxYearStart } from './tax-year.js';
/** Whole pounds for label/warning copy, e.g. 5000000 → "£50,000". */
function gbpWhole(penceValue) {
    return `£${Math.trunc(penceValue / 100).toLocaleString('en-GB')}`;
}
/** amount × bps / 10000, truncated toward zero; refuses unsafe magnitudes. */
function bpsOf(amount, bps) {
    const out = Math.trunc((amount * bps) / 10_000);
    if (!Number.isSafeInteger(out)) {
        throw new RangeError('A planned-event amount escalated beyond the safe integer range');
    }
    return out;
}
function compound(base, bps, years) {
    let acc = base;
    for (let i = 0; i < years; i += 1) {
        acc += bpsOf(acc, bps);
        if (!Number.isSafeInteger(acc)) {
            throw new RangeError('A planned-event amount escalated beyond the safe integer range');
        }
    }
    return acc;
}
function oneOffSpend(id, label, year, amount, 
/**
 * A2.4. An education plan the household pays FOR SOMEONE ELSE can be routed
 * to the seven-year clock. It rides the gift flag A2.7 already put on a
 * spending line, so there is no second gift-collection path to keep in step —
 * the fee is spending either way, and the flag only records what it was.
 */
gift) {
    return {
        id,
        label,
        amount: pence(amount),
        frequency: Frequency.Once,
        startYear: year,
        endYear: year,
        inflationLinked: false, // amount is already escalated at expansion time
        /**
         * A2.5a review (adviser). An expanded planned event is NEVER scaled by the
         * survivor spending factor. A widow does not pay 70% of her grandchild's
         * school fees or 70% of her own care home, and a scaled recurring gift
         * would move an inheritance-tax figure off a spending assumption. Marked
         * explicitly rather than left to `undefined`, which the engine reads as
         * `joint` and therefore scales.
         */
        ownerId: 'household',
        ...(gift === undefined
            ? {}
            : {
                isGift: true,
                ...(gift.recipient !== undefined ? { giftRecipient: gift.recipient } : {}),
                ...(gift.ihtTreatment === 'normalExpenditureOutOfIncome'
                    ? { exemptionClaimed: 'normalExpenditureOutOfIncome' }
                    : gift.exemptionClaimed !== undefined
                        ? { exemptionClaimed: gift.exemptionClaimed }
                        : {}),
            }),
    };
}
/**
 * A2.4. `thirdParty` means the fees are somebody else's outflow, so they are
 * not this household's spending at all; `undefined` and `household` both mean
 * this household pays. Returns the gift routing when there is one.
 */
function educationTreatment(plan) {
    if (plan.paidBy === 'thirdParty')
        return { paid: false };
    return plan.ihtTreatment === 'gift' || plan.ihtTreatment === 'normalExpenditureOutOfIncome'
        ? {
            paid: true,
            gift: {
                ihtTreatment: plan.ihtTreatment,
                // A2.4 review: `recipient` was accepted and never supplied, so fee
                // gifts rendered blank in the gift table A2.1 built to NAME who a
                // gift went to. The dependent was on hand the whole time.
                ...(plan.recipientLabel !== undefined ? { recipient: plan.recipientLabel } : {}),
                // A2.4 review: the adviser's choice reaches the fee gift, so the
                // £3,000 is not handed out by array order (life events before fees).
                ...(plan.exemptionClaimed !== undefined
                    ? { exemptionClaimed: plan.exemptionClaimed }
                    : {}),
            },
        }
        : { paid: true };
}
function dependentOf(dependents, id, planLabel) {
    const dep = (dependents ?? []).find((d) => d.id === id);
    if (dep === undefined) {
        throw new RangeError(`Planned event "${planLabel}" names an unknown dependent "${id}"`);
    }
    return dep;
}
/** The tax year the dependent reaches `age` (age measured at the tax-year start). */
function yearAtAge(dep, startYear, age) {
    return startYear + (age - ageAtTaxYearStart(dep.dateOfBirth, startYear));
}
const DEFAULT_SCHOOL_ESCALATION = { kind: 'cpiPlus', deltaBps: 200 };
const DEFAULT_UNIVERSITY_ESCALATION = { kind: 'cpiPlus', deltaBps: 0 };
const DEFAULT_CARE_ESCALATION = { kind: 'cpiPlus', deltaBps: 150 };
/**
 * The years a planned event occupies, by the SAME rules `expandPlannedEvents`
 * charges it — one definition, so a chart that marks an event can never
 * disagree with the projection about when it happens (workspace v2 §11.2).
 * Pure; returns null where the plan names a person or dependent the household
 * does not have, or a repeat interval that is not a whole year (the expansion
 * refuses those outright).
 */
export function plannedEventWindow(plan, ctx) {
    switch (plan.kind) {
        case 'schoolFees': {
            const dep = ctx.dependents.find((d) => d.id === plan.dependentId);
            if (dep === undefined)
                return null;
            return {
                kind: 'span',
                from: plan.startYear ?? yearAtAge(dep, ctx.startYear, plan.startAge ?? 4),
                to: plan.endYear ?? yearAtAge(dep, ctx.startYear, plan.endAge ?? 18) - 1,
            };
        }
        case 'university': {
            const dep = ctx.dependents.find((d) => d.id === plan.dependentId);
            if (dep === undefined)
                return null;
            const from = plan.startYear ?? yearAtAge(dep, ctx.startYear, plan.startAge ?? 18);
            return { kind: 'span', from, to: from + (plan.durationYears ?? 3) - 1 };
        }
        case 'care': {
            const person = ctx.people.find((p) => p.id === plan.personId);
            if (person === undefined)
                return null;
            const from = plan.startYear ?? ctx.startYear + ((plan.startAge ?? 0) - person.currentAge);
            // A fixed duration, else until the person's modelled death/horizon.
            const untilDeath = person.deathAge !== null
                ? ctx.startYear + (person.deathAge - person.currentAge)
                : ctx.lastYear;
            const to = plan.durationYears != null
                ? Math.min(from + plan.durationYears - 1, untilDeath)
                : untilDeath;
            return { kind: 'span', from, to };
        }
        case 'careerBreak': {
            const person = ctx.people.find((p) => p.id === plan.personId);
            if (person === undefined)
                return null;
            const from = plan.startYear ?? ctx.startYear + ((plan.startAge ?? 0) - person.currentAge);
            return { kind: 'span', from, to: from + plan.durationYears - 1 };
        }
        case 'recurring': {
            if (!Number.isInteger(plan.everyNYears) || plan.everyNYears < 1)
                return null;
            const to = Math.min(plan.endYear ?? ctx.lastYear, ctx.lastYear);
            const years = [];
            for (let year = Math.max(plan.startYear, ctx.startYear); year <= to; year += plan.everyNYears) {
                years.push(year);
            }
            return { kind: 'repeating', years };
        }
        case 'propertyPurchase':
        case 'propertySale':
        case 'businessSale':
            return { kind: 'once', year: plan.year };
        default: {
            const _exhaustive = plan;
            void _exhaustive;
            return null;
        }
    }
}
/** A span the expansion has already checked can resolve (its person or dependent exists). */
function spanOf(window) {
    if (window === null || window.kind !== 'span') {
        throw new RangeError('A planned event has no resolvable window');
    }
    return window;
}
export function expandPlannedEvents(input, startYear, horizonYears, escCtxFor, rules) {
    const plans = input.plannedEvents ?? [];
    const spending = [];
    const lifeEvents = [];
    const incomeStreams = [];
    const suppressions = [];
    const warnings = [];
    const lastYear = startYear + horizonYears - 1;
    const windowCtx = {
        startYear,
        lastYear,
        people: input.people,
        dependents: input.dependents ?? [],
    };
    let presetFiguresUsed = false;
    // ESCALATION ANCHOR: every planned-event amount is entered at TODAY'S
    // prices (the valuation date), so escalation compounds from the projection
    // start year — a plan starting in year N is charged N years of escalation
    // in its first year, and a window already under way is charged today's
    // figure now (never back-compounded from a past start).
    const escalatedTo = (base, esc, year) => {
        // Compound year-by-year from the start year so each year picks BOTH its
        // active stepped rate (FEATURES §0.1d/e) AND that year's scheduled
        // assumption context; constant contexts/rates reduce to the old one-shot
        // compound, penny-identical.
        let acc = base;
        for (let y = startYear + 1; y <= year; y += 1) {
            acc = compound(acc, resolveEscalationBpsForYear(esc, y, escCtxFor(y)), 1);
        }
        return acc;
    };
    // School-plan windows are resolved first so sibling discounts can see
    // which OTHER children's fee-charging plans are active in a given year.
    const schoolWindows = new Map();
    for (const plan of plans) {
        if (plan.kind !== 'schoolFees')
            continue;
        dependentOf(input.dependents, plan.dependentId, plan.label);
        const { from, to } = spanOf(plannedEventWindow(plan, windowCtx));
        schoolWindows.set(plan.id, {
            from,
            to,
            dependentId: plan.dependentId,
            hasFee: plan.feePerTerm > 0,
        });
    }
    for (const plan of plans) {
        if ('presetId' in plan && plan.presetId != null)
            presetFiguresUsed = true;
        switch (plan.kind) {
            case 'schoolFees': {
                const window = schoolWindows.get(plan.id);
                const schoolEsc = plan.escalation ?? DEFAULT_SCHOOL_ESCALATION;
                const annualBase = plan.feePerTerm * (plan.termsPerYear ?? 3);
                for (let year = Math.max(window.from, startYear); year <= Math.min(window.to, lastYear); year += 1) {
                    let amount = escalatedTo(annualBase, schoolEsc, year);
                    // A sibling discount needs a DIFFERENT child actually being charged
                    // — a same-child phase (prep → senior) or a £0 state place is not one.
                    const siblingAtSchool = [...schoolWindows.values()].some((w) => w.dependentId !== plan.dependentId && w.hasFee && year >= w.from && year <= w.to);
                    if (siblingAtSchool && (plan.siblingDiscountBps ?? 0) > 0) {
                        amount -= bpsOf(amount, plan.siblingDiscountBps);
                    }
                    if (amount <= 0)
                        continue;
                    const t = educationTreatment({
                        ...plan,
                        recipientLabel: dependentOf(input.dependents, plan.dependentId, plan.label).label,
                    });
                    if (!t.paid)
                        continue;
                    spending.push(oneOffSpend(`${plan.id}:${year}`, plan.label, year, amount, t.gift));
                }
                break;
            }
            case 'university': {
                dependentOf(input.dependents, plan.dependentId, plan.label);
                const window = spanOf(plannedEventWindow(plan, windowCtx));
                const from = window.from;
                const duration = window.to - window.from + 1;
                const uniEsc = plan.escalation ?? DEFAULT_UNIVERSITY_ESCALATION;
                const share = plan.parentalContributionBps ?? 10_000;
                const annualBase = bpsOf(plan.annualTuition + plan.annualMaintenance, share);
                for (let i = 0; i < duration; i += 1) {
                    const year = from + i;
                    if (year < startYear || year > lastYear)
                        continue;
                    const amount = escalatedTo(annualBase, uniEsc, year);
                    if (amount <= 0)
                        continue;
                    const t = educationTreatment({
                        ...plan,
                        recipientLabel: dependentOf(input.dependents, plan.dependentId, plan.label).label,
                    });
                    if (!t.paid)
                        continue;
                    spending.push(oneOffSpend(`${plan.id}:${year}`, plan.label, year, amount, t.gift));
                }
                break;
            }
            case 'care': {
                const person = input.people.find((p) => p.id === plan.personId);
                if (person === undefined) {
                    throw new RangeError(`Care plan "${plan.label}" names an absent person`);
                }
                // A fixed duration, else until the person's modelled death/horizon.
                const { from, to } = spanOf(plannedEventWindow(plan, windowCtx));
                const careEsc = plan.escalation ?? DEFAULT_CARE_ESCALATION;
                for (let year = Math.max(from, startYear); year <= Math.min(to, lastYear); year += 1) {
                    const amount = escalatedTo(plan.annualCost, careEsc, year);
                    if (amount <= 0)
                        continue;
                    spending.push(oneOffSpend(`${plan.id}:${year}`, plan.label, year, amount));
                }
                break;
            }
            case 'careerBreak': {
                const person = input.people.find((p) => p.id === plan.personId);
                if (person === undefined) {
                    throw new RangeError(`Career break "${plan.label}" names an absent person`);
                }
                const { from, to } = spanOf(plannedEventWindow(plan, windowCtx));
                suppressions.push({
                    personId: plan.personId,
                    fromYear: from,
                    toYear: to,
                });
                const lump = plan.redundancyLumpSum ?? 0;
                if (lump > 0) {
                    const exempt = Math.min(lump, rules.incomeTax.redundancyExemption ?? 0);
                    if (exempt > 0) {
                        lifeEvents.push({
                            id: `${plan.id}:exempt`,
                            label: `${plan.label} — tax-free redundancy pay`,
                            year: from,
                            amount: pence(exempt),
                            direction: 'inflow',
                            wrapper: null,
                        });
                    }
                    const excess = lump - exempt;
                    if (excess > 0) {
                        // The slice above the statutory exemption is taxed as income.
                        incomeStreams.push({
                            id: `${plan.id}:excess`,
                            label: `${plan.label} — taxable redundancy pay`,
                            ownerId: plan.personId,
                            type: 'other',
                            amount: pence(excess),
                            frequency: Frequency.Once,
                            startYear: from,
                            endYear: from,
                            endsAtRetirement: false,
                            growthRateBps: 0,
                            taxable: true,
                        });
                    }
                }
                break;
            }
            case 'recurring': {
                if (!Number.isInteger(plan.everyNYears) || plan.everyNYears < 1) {
                    throw new RangeError(`Recurring plan "${plan.label}" needs a whole-year interval`);
                }
                const occurrences = plannedEventWindow(plan, windowCtx);
                if (occurrences === null || occurrences.kind !== 'repeating') {
                    throw new RangeError(`Recurring plan "${plan.label}" has no resolvable occurrences`);
                }
                const recurringEsc = plan.escalation ?? { kind: 'none' };
                if ((plan.direction ?? 'outflow') === 'inflow') {
                    // Recurring inflows are UNTAXED by design (a generic receipt);
                    // anything with a tax character must be an income stream instead.
                    warnings.push({
                        code: 'recurring-inflow-untaxed',
                        key: `recurring-inflow:${plan.id}`,
                        message: `"${plan.label}" is modelled as an untaxed recurring receipt — if it has a tax character (earnings, rent, trust income), model it as an income stream instead.`,
                    });
                }
                for (const year of occurrences.years) {
                    const amount = escalatedTo(plan.amount, recurringEsc, year);
                    if (amount <= 0)
                        continue;
                    if ((plan.direction ?? 'outflow') === 'outflow') {
                        spending.push(oneOffSpend(`${plan.id}:${year}`, plan.label, year, amount));
                    }
                    else {
                        lifeEvents.push({
                            id: `${plan.id}:${year}`,
                            label: plan.label,
                            year,
                            amount: pence(amount),
                            direction: 'inflow',
                            wrapper: null,
                        });
                    }
                }
                break;
            }
            case 'propertyPurchase': {
                // The deposit and stamp duty are known amounts (price is a plan
                // parameter), so the OUTFLOW is expanded here; the property/mortgage
                // creation is engine-native (household.ts). Stamp duty uses the
                // versioned rule set held constant — a future purchase pays today's
                // bands (illustrative, like the IHT estimate).
                if (plan.deposit > plan.price) {
                    throw new RangeError(`Purchase "${plan.label}": deposit exceeds the price`);
                }
                const sdlt = stampDutyLandTax(rules, {
                    purchasePrice: pence(plan.price),
                    isFirstTimeBuyer: plan.isFirstTimeBuyer ?? false,
                    isAdditionalDwelling: plan.isAdditionalDwelling ?? plan.isBuyToLet ?? false,
                });
                if (plan.year >= startYear && plan.year <= lastYear) {
                    // Deposit and stamp duty are SEPARATE lines so the modelled tax
                    // amount is visible, not hidden inside a combined figure.
                    if (plan.deposit > 0) {
                        lifeEvents.push({
                            id: `${plan.id}:deposit`,
                            label: `${plan.label} — deposit`,
                            year: plan.year,
                            amount: pence(plan.deposit),
                            direction: 'outflow',
                            wrapper: null,
                        });
                    }
                    if (sdlt.taxDue > 0) {
                        lifeEvents.push({
                            id: `${plan.id}:sdlt`,
                            label: `${plan.label} — stamp duty (${gbpWhole(sdlt.taxDue)}, today's bands held constant)`,
                            year: plan.year,
                            amount: pence(sdlt.taxDue),
                            direction: 'outflow',
                            wrapper: null,
                        });
                    }
                }
                break;
            }
            // Engine-native kinds — applied inside household.ts.
            case 'propertySale':
            case 'businessSale':
                break;
            default: {
                const _exhaustive = plan;
                void _exhaustive;
            }
        }
    }
    if (presetFiguresUsed) {
        warnings.push({
            code: 'preset-figures-pending-review',
            key: 'preset-figures-pending-review',
            message: 'One or more planned events use indicative preset fee/cost levels — editable starting points pending the firm’s sign-off, not quotes or endorsements.',
        });
    }
    return { spending, lifeEvents, incomeStreams, suppressions, warnings };
}
//# sourceMappingURL=planned-events.js.map